/**
 * Words Navigation
 *
 * Shared navigation component for words management pages (All Words and Word Lists tabs)
 * Includes a language context dropdown that filters both views.
 */

import { useEffect } from 'react';
import { NavLink, useLocation, useSearchParams } from 'react-router-dom';
import { useInitialData } from '../../../contexts/InitialDataContext';

export function WordsNavigation() {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { state: initialDataState } = useInitialData();
  const languages = (initialDataState.data?.languages ?? []).filter(
    (lang) => lang.code !== 'zxx' && lang.code !== 'other'
  );

  // Get selected language from URL, default to first available language
  const selectedLanguage = searchParams.get('lang') || '';
  const defaultLanguage = languages[0]?.code || '';

  // Auto-set language if not specified in URL
  useEffect(() => {
    if (!selectedLanguage && defaultLanguage) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('lang', defaultLanguage);
      setSearchParams(newParams, { replace: true });
    }
  }, [selectedLanguage, defaultLanguage, searchParams, setSearchParams]);

  // Use effective language (URL param or default)
  const effectiveLanguage = selectedLanguage || defaultLanguage;

  const navItems = [
    { name: 'All Words', path: '/words' },
    { name: 'Word Lists', path: '/words/lists' },
    { name: 'Generate List', path: '/words/lists/generate' },
    { name: 'Generate Ideas', path: '/words/lists/ideas' },
  ];

  // Helper function to determine if a nav item should be active
  const isNavItemActive = (itemPath: string) => {
    if (itemPath === '/words') {
      // "All Words" is active only for exact /words path
      return location.pathname === '/words';
    }
    if (itemPath === '/words/lists/ideas') {
      // "Generate Ideas" is active for /words/lists/ideas path
      return location.pathname === '/words/lists/ideas';
    }
    if (itemPath === '/words/lists/generate') {
      // "Generate List" is active for /words/lists/generate path
      return location.pathname === '/words/lists/generate';
    }
    if (itemPath === '/words/lists') {
      // "Word Lists" is active for /words/lists and sub-paths, except generate pages
      return (
        location.pathname.startsWith('/words/lists') &&
        !location.pathname.startsWith('/words/lists/ideas') &&
        !location.pathname.startsWith('/words/lists/generate')
      );
    }
    return location.pathname.startsWith(itemPath);
  };

  const handleLanguageChange = (langCode: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (langCode) {
      newParams.set('lang', langCode);
    } else {
      newParams.delete('lang');
    }
    setSearchParams(newParams);
  };

  // Build path with current lang param preserved
  const buildPathWithLang = (basePath: string) => {
    if (effectiveLanguage) {
      return `${basePath}?lang=${effectiveLanguage}`;
    }
    return basePath;
  };

  return (
    <div className="mb-4">
      {/* Language Context Dropdown */}
      <div className="mb-4 flex items-center gap-3">
        <label className="text-sm font-medium text-gray-700">Language:</label>
        <select
          value={effectiveLanguage}
          onChange={(e) => handleLanguageChange(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white min-w-[200px]"
        >
          {languages.map((lang) => (
            <option key={lang.code} value={lang.code}>
              {lang.name}
            </option>
          ))}
        </select>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {navItems.map((item) => {
            const isActive = isNavItemActive(item.path);
            return (
              <NavLink
                key={item.path}
                to={buildPathWithLang(item.path)}
                className={`whitespace-nowrap border-b-2 py-3 px-1 text-sm font-medium transition-colors ${
                  isActive
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                }`}
              >
                {item.name}
              </NavLink>
            );
          })}
        </nav>
      </div>
    </div>
  );
}
